/**
 * Import/Export Panel Component
 *
 * Provides UI controls for importing and exporting configurations, BOMs, and topologies.
 * Integrates with the io module for CSV, Markdown, DOT, and JSON operations.
 */

import { useState, useRef } from 'react';
import {
  parseCSVFile,
  downloadMarkdownBOM,
  downloadDOT,
  downloadJSON,
  saveToLocalStorage,
  loadFromLocalStorage,
  loadConfigList,
  deleteConfig,
  type ImportResult,
} from '../../io';
import { exportNvidiaAirTopology } from '../../io/nvidia-air';
import { useStore } from '../../store/store';

interface ImportExportPanelProps {
  className?: string;
}

export function ImportExportPanel({ className = '' }: ImportExportPanelProps) {
  const [importing, setImporting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [savedConfigs, setSavedConfigs] = useState(loadConfigList());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const configuration = useStore((state) => state.configuration);
  const setConfiguration = useStore((state) => state.setConfiguration);
  const bom = useStore((state) => state.bom);

  // Debug BOM access
  console.log('[ImportExportPanel] BOM from store:', {
    hasBOM: !!bom,
    nodeCount: bom?.summary?.compute?.totalNodes,
  });

  // Clear messages after timeout
  const clearMessages = () => {
    setTimeout(() => {
      setErrors([]);
      setSuccessMessage('');
    }, 5000);
  };

  // Handle CSV file import
  const handleCSVImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setErrors([]);
    setSuccessMessage('');

    try {
      const result = await parseCSVFile(file);

      if (result.success && result.data) {
        setSuccessMessage(`Successfully imported ${result.data.length} server profiles`);
        // Note: Server profiles would be stored in a separate store slice
        // For now, just show success message
      } else if (result.errors) {
        const errorMessages = result.errors.map((e) => `Row ${e.row}: ${e.message}`);
        setErrors(errorMessages);
      }
    } catch (error) {
      setErrors(['Failed to import CSV file. Please check the file format.']);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      clearMessages();
    }
  };

  // Handle Markdown export
  const handleMarkdownExport = () => {
    try {
      if (!bom) {
        setErrors(['No BOM data available. Please run calculations first.']);
        clearMessages();
        return;
      }

      downloadMarkdownBOM(
        bom,
        configuration,
        `gpu-cluster-bom-${new Date().toISOString().split('T')[0]}.md`,
        {
          includeMetadata: true,
          includeDerivations: true,
          includeTOC: true,
        }
      );
      setSuccessMessage('BOM exported as Markdown');
      clearMessages();
    } catch (error) {
      setErrors(['Failed to export Markdown. Please try again.']);
      clearMessages();
    }
  };

  // Handle DOT export
  const handleDOTExport = () => {
    try {
      if (!bom) {
        setErrors(['No BOM data available. Please run calculations first.']);
        clearMessages();
        return;
      }

      downloadDOT(
        bom,
        configuration,
        `cluster-topology-${new Date().toISOString().split('T')[0]}.dot`,
        {
          includeInfiniBand: true,
          includeEthernet: true,
          includeOOB: true,
          includeComments: true,
        }
      );
      setSuccessMessage('Topology exported for NVIDIA Air');
      clearMessages();
    } catch (error) {
      console.error('DOT export error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setErrors([`Failed to export DOT topology: ${errorMsg}`]);
      clearMessages();
    }
  };

  // Handle NVIDIA Air topology export
  const handleJSONExport = () => {
    try {
      if (!bom) {
        setErrors(['No BOM data available. Please run calculations first.']);
        clearMessages();
        return;
      }

      const airTopology = exportNvidiaAirTopology(bom, configuration);
      const jsonStr = JSON.stringify(airTopology, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nvidia-air-topology-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccessMessage('NVIDIA Air topology exported successfully');
      clearMessages();
    } catch (error) {
      console.error('NVIDIA Air export error:', error);
      setErrors(['Failed to export NVIDIA Air topology. Please try again.']);
      clearMessages();
    }
  };

  // Handle save to localStorage
  const handleSave = () => {
    try {
      const name = prompt('Enter a name for this configuration:');
      if (!name) return;

      saveToLocalStorage(configuration, name);
      setSavedConfigs(loadConfigList());
      setSuccessMessage(`Configuration "${name}" saved successfully`);
      clearMessages();
    } catch (error) {
      setErrors(['Failed to save configuration. Storage may be full.']);
      clearMessages();
    }
  };

  // Handle load from localStorage
  const handleLoad = (id: string) => {
    try {
      const loaded = loadFromLocalStorage();
      if (loaded) {
        setConfiguration(loaded.config);
        setSuccessMessage(`Configuration "${loaded.name}" loaded successfully`);
        clearMessages();
      }
    } catch (error) {
      setErrors(['Failed to load configuration.']);
      clearMessages();
    }
  };

  // Handle delete configuration
  const handleDelete = (id: string) => {
    try {
      deleteConfig(id);
      setSavedConfigs(loadConfigList());
      setSuccessMessage('Configuration deleted successfully');
      clearMessages();
    } catch (error) {
      setErrors(['Failed to delete configuration.']);
      clearMessages();
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Import / Export</h3>

      {/* Messages */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">
          {successMessage}
        </div>
      )}

      {errors.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
          {errors.map((error, i) => (
            <div key={i} className="text-red-800 text-sm">
              {error}
            </div>
          ))}
        </div>
      )}

      {/* Import Section */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Import</h4>
        <div className="space-y-2">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleCSVImport}
              disabled={importing}
              className="hidden"
              id="csv-file-input"
            />
            <label
              htmlFor="csv-file-input"
              className={`inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded cursor-pointer hover:bg-blue-700 transition-colors ${
                importing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {importing ? 'Importing...' : 'Import Server Profiles (CSV)'}
            </label>
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Export</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            onClick={handleMarkdownExport}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
          >
            Export BOM (Markdown)
          </button>
          <button
            onClick={handleJSONExport}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
          >
            Export NVIDIA Air Topology (JSON)
          </button>
        </div>
      </div>

      {/* Save/Load Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Save / Load</h4>
        <div className="space-y-2">
          <button
            onClick={handleSave}
            className="w-full px-4 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
          >
            Save Current Configuration
          </button>

          {savedConfigs.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="text-xs font-semibold text-gray-600 uppercase">Saved Configurations</div>
              {savedConfigs.map((config) => (
                <div
                  key={config.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{config.name}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(config.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleLoad(config.id)}
                      className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${config.name}"?`)) {
                          handleDelete(config.id);
                        }
                      }}
                      className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
